/**
 * Doserne — SJA archive / management.
 * Lists every SJA with status (Utkast / Signeres / Sendt) + signature count.
 * Admins can reopen a sent SJA for MORE signatures and (re)send the PDF —
 * regenerated with all current signatures.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ShieldAlert, Plus, Search, Download, RotateCcw, Send,
  Copy, CheckCircle2, PenLine, ExternalLink,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft:   { label: "Utkast",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  signing: { label: "Signeres", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  sent:    { label: "Sendt",    cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const FILTERS = [
  { key: "",        label: "Alle" },
  { key: "signing", label: "Signeres" },
  { key: "sent",    label: "Sendt" },
  { key: "draft",   label: "Utkast" },
];

function StatusBadge({ status, reopened }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black border rounded-full px-2 py-0.5 ${m.cls}`}>
      {m.label}{reopened && status === "signing" ? " ↺" : ""}
    </span>
  );
}

function Row({ item, authHeader, onChanged }) {
  const [busy, setBusy] = useState("");
  const signUrl = `${window.location.origin}/apps/sja/sign/${item.sign_token}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(signUrl); toast.success("Signeringslenke kopiert"); }
    catch { toast.error("Kopiering mislyktes"); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await axios.get(`${API}/sja/doserne/${item.sja_id}/pdf`, { ...authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${item.code || "sja"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Kunne ikke laste ned PDF"); }
    finally { setBusy(""); }
  };

  const reopen = async () => {
    if (!window.confirm("Gjenåpne SJA for flere signaturer? Innholdet kan ikke endres.")) return;
    setBusy("reopen");
    try {
      await axios.post(`${API}/sja/doserne/${item.sja_id}/reopen`, {}, authHeader());
      toast.success("SJA gjenåpnet — del signeringslenken");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Feil"); }
    finally { setBusy(""); }
  };

  const send = async () => {
    const label = item.status === "sent" ? "Send SJA på nytt?" : "Send SJA?";
    if (!window.confirm(`${label} PDF med ${item.signed_count} signatur(er) sendes på e-post.`)) return;
    setBusy("send");
    try {
      const { data } = await axios.post(`${API}/sja/doserne/${item.sja_id}/send`, {}, authHeader());
      toast.success(`Sendt — PDF med ${data.signed_count} signatur(er)`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Feil"); }
    finally { setBusy(""); }
  };

  return (
    <div className="p-4 hover:bg-slate-50 transition" data-testid={`sja-row-${item.sja_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-mono font-black text-sm text-slate-900">{item.code}</span>
            <StatusBadge status={item.status} reopened={item.reopened} />
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500">
              <PenLine className="w-3.5 h-3.5" /> {item.signed_count} signert
            </span>
          </div>
          <div className="text-sm font-bold text-slate-800 truncate">{item.job_description || "SJA"}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {[item.work_location || item.address, item.work_date, item.prepared_by].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap mt-3">
        <a href={signUrl} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-xs font-bold text-slate-700"
           data-testid={`sja-open-${item.sja_id}`}>
          <ExternalLink className="w-3.5 h-3.5" /> Åpne
        </a>
        <button onClick={copyLink}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-xs font-bold text-slate-700"
          data-testid={`sja-copy-${item.sja_id}`}>
          <Copy className="w-3.5 h-3.5" /> Kopier lenke
        </button>
        <button onClick={downloadPdf} disabled={busy === "pdf"}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-xs font-bold text-slate-700 disabled:opacity-50"
          data-testid={`sja-pdf-${item.sja_id}`}>
          {busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
        </button>
        {item.status === "sent" && (
          <button onClick={reopen} disabled={busy === "reopen"}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-xs font-black text-amber-800 disabled:opacity-50"
            data-testid={`sja-reopen-${item.sja_id}`}>
            {busy === "reopen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Gjenåpne
          </button>
        )}
        {item.signed_count > 0 && (
          <button onClick={send} disabled={busy === "send"}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-xs font-black text-white disabled:opacity-50"
            style={{ color: "#fff" }}
            data-testid={`sja-send-${item.sja_id}`}>
            {busy === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {item.status === "sent" ? "Send på nytt" : "Send"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DoserneSJA() {
  const { user, loading: authLoading, authHeader } = useDoserneAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const isAdmin = !!user?.is_admin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sja/doserne/list`, { ...authHeader(), params: { q, status } });
      setItems(data.items || []);
    } catch { toast.error("Kunne ikke laste SJA"); }
    setLoading(false);
  }, [authHeader, q, status]);

  useEffect(() => { if (user && isAdmin) load(); }, [user, isAdmin, load]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  return (
    <DoserneShell>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900">SJA</h1>
            <div className="text-[11px] text-slate-500">Sikker jobb-analyse — arkiv & signering</div>
          </div>
        </div>
        <Link to="/apps/sja"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-black"
          style={{ color: "#fff" }} data-testid="sja-new-btn">
          <Plus className="w-4 h-4" /> Ny SJA
        </Link>
      </div>

      {!isAdmin ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-black text-slate-700">Lag en ny SJA</div>
          <div className="text-xs text-slate-500 mt-1">Trykk <strong>Ny SJA</strong> for å starte. Arkivet er kun for admin.</div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Søk kode, jobb, adresse…"
                className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-xl text-sm" data-testid="sja-search" />
            </div>
            <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
              {FILTERS.map((f) => (
                <button key={f.key} onClick={() => setStatus(f.key)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-black transition ${status === f.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  data-testid={`sja-filter-${f.key || "all"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 px-4">
                <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <div className="text-sm font-black text-slate-700">Ingen SJA {status ? "med denne statusen" : "ennå"}</div>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {items.map((it) => (
                  <Row key={it.sja_id} item={it} authHeader={authHeader} onChanged={load} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </DoserneShell>
  );
}
